/**
 * Segregation-of-duties engine — V98-1-9 / ADR-0046 §4–§6, Appendix A T1,
 * T14, T15, T17; research §2 rules 5–8, 12, 13, 16, 20 (migration 239).
 *
 * - **Pairs are data** (`SOD_PAIRS`), not code branches. A step (approve,
 *   co-approve, accept a risk, review an access row, approve an access row,
 *   check) names the role it acts as; the item names its parties (preparer,
 *   policy author, review subject, …). `conflictsFor` reads the pairs to say
 *   who may not act.
 * - **Hats** are the union of a person's current assignments (`hatsOf`); there
 *   is no role switcher.
 * - **Routing (T1)** walks: holder → their delegate (only when the holder is
 *   absent and a live delegation covers the item's category) → backup → the
 *   holder's escalation chain (manager → CEO → board / audit committee, and a
 *   board-authorized alternate signer). It skips absent people, anyone in a
 *   conflicting pair, non-members, audit-firm identities (T17) and
 *   contact-only chain entries. It never returns a party of the item
 *   (preparer / author / subject). With no `approver` / `backup_approver`
 *   assignment on the subject at all (live v9.6 data), it falls back to an
 *   eligible owner / admin (owners first, deterministic), then the
 *   workspace's independent party — same eligibility rules, never for
 *   non-delegable signatures. No candidate → `none_eligible` + reasons.
 *   A delegate never satisfies a pair against the delegator: a conflicted
 *   holder is skipped before their delegation is considered, and a
 *   conflicted delegate never falls back to the same person.
 * - **Access reviews (T14/T15)**: each row's reviewer ≠ its subject (a
 *   self-subject row routes per T1); a row's approver never reviewed it and
 *   isn't its subject; reciprocal pairs (A reviews B while B reviews A) are
 *   flagged and go to a third holder.
 * - **Same-person completeness** (derived, auditor tier): every approval in
 *   the period, evaluated against every pair at row level. History that
 *   can't be evaluated is `not_determinable`, never complete. A second source
 *   (the append-only event logs) reconciles the population count (IPE).
 *
 * Writes here are limited to the access-review row subject map (insert-once)
 * and the append-only `sod_events` log. Coverage transitions (T2–T13, T16)
 * live in coverage-service.
 */
import { generateRandomId } from '../utils/audit-crypto.js';
import { isAuditFirmIdentity, listHats, } from './assignment-service.js';
import { sqlIn } from '../utils/sql-in.js';
// ---- pairs ---------------------------------------------------------------------
/** Roles an item's parties play. */
export const SOD_PARTY_ROLES = [
    'preparer', 'policy_author', 'access_reviewer', 'review_subject', 'scn_classifier', 'risk_owner',
];
/** Roles a routed step acts as. The first four are the approver family. */
export const SOD_STEP_ROLES = [
    'approver', 'co_approver', 'acceptance_approver', 'row_approver', 'access_reviewer', 'checker',
];
const APPROVER_FAMILY = new Set(['approver', 'co_approver', 'acceptance_approver', 'row_approver']);
/** ADR-0046 §4 — the SoD pairs, as data. */
export const SOD_PAIRS = [
    { id: 'preparer_approver', label: 'Preparer and approver', party: 'preparer', step: 'approver_family', enforced_by: 'item', source: 'ADR-0046 §4; research §2 item 5' },
    { id: 'policy_author_approver', label: 'Policy author and approver', party: 'policy_author', step: 'approver_family', enforced_by: 'item', source: 'ADR-0046 §4; research §2 item 5' },
    { id: 'access_reviewer_subject', label: 'Access reviewer and subject of the review (per row)', party: 'review_subject', step: 'access_reviewer', enforced_by: 'item', source: 'ADR-0046 §4, T14; research §2 item 13' },
    { id: 'row_reviewer_approver', label: 'Approver and the row they reviewed', party: 'access_reviewer', step: 'row_approver', enforced_by: 'item', source: 'ADR-0046 §4, T15; research §2 item 13' },
    { id: 'row_subject_approver', label: 'Row subject and the row approver', party: 'review_subject', step: 'row_approver', enforced_by: 'item', source: 'ADR-0046 T15; research §2 item 13' },
    { id: 'scn_classifier_co_approver', label: 'Change-notice classifier and co-approver', party: 'scn_classifier', step: 'co_approver', enforced_by: 'item', source: 'ADR-0046 §4; research §2 item 5' },
    { id: 'risk_owner_acceptance_approver', label: 'Risk owner and approver of its acceptance', party: 'risk_owner', step: 'acceptance_approver', enforced_by: 'item', source: 'ADR-0046 §4; research §2 items 5–6' },
    { id: 'reciprocal_reviewers', label: 'Reciprocal reviewers (A reviews B while B reviews A)', party: 'access_reviewer', step: 'access_reviewer', enforced_by: 'row_routing', source: 'ADR-0046 §4, T14; research §2 item 13' },
    { id: 'audit_firm_client_hat', label: 'Audit-firm member and any client hat', party: 'audit_firm_member', step: 'client_hat', enforced_by: 'identity', source: 'ADR-0046 §4, T17; ADR-0047 R2' },
];
const PAIR_BY_ID = new Map(SOD_PAIRS.map((p) => [p.id, p]));
export function getSodPair(id) {
    return PAIR_BY_ID.get(id);
}
function stepMatches(pair, step) {
    if (pair.step === 'approver_family')
        return APPROVER_FAMILY.has(step);
    return pair.step === step;
}
/**
 * Who may not take this step on this item, and through which pair(s). A
 * checker (T10) must be outside every pair on the item, so every party
 * conflicts with the checker step.
 */
export function conflictsFor(step, parties) {
    const out = new Map();
    const add = (u, pairId) => {
        if (!u)
            return;
        const l = out.get(u) ?? [];
        if (!l.includes(pairId))
            l.push(pairId);
        out.set(u, l);
    };
    for (const pair of SOD_PAIRS) {
        if (pair.enforced_by !== 'item' || pair.party === 'audit_firm_member')
            continue;
        if (!stepMatches(pair, step))
            continue;
        for (const u of parties[pair.party] ?? [])
            add(u, pair.id);
    }
    if (step === 'checker') {
        for (const role of SOD_PARTY_ROLES)
            for (const u of parties[role] ?? [])
                add(u, 'checker_independence');
    }
    // Universal: whoever prepared the item never approves it, whatever the step.
    for (const u of parties.preparer ?? [])
        add(u, 'preparer_approver');
    return out;
}
/** The step role and party mapping for a signature request of this artifact kind. */
export function signatureStep(artifactKind, preparer) {
    switch (artifactKind) {
        case 'policy': return { step: 'approver', parties: { preparer: [preparer], policy_author: [preparer] }, pair_id: 'policy_author_approver' };
        case 'risk_acceptance': return { step: 'acceptance_approver', parties: { preparer: [preparer], risk_owner: [preparer] }, pair_id: 'risk_owner_acceptance_approver' };
        case 'scn': return { step: 'co_approver', parties: { preparer: [preparer], scn_classifier: [preparer] }, pair_id: 'scn_classifier_co_approver' };
        default: return { step: 'approver', parties: { preparer: [preparer] }, pair_id: 'preparer_approver' };
    }
}
// ---- coverage categories ---------------------------------------------------------
/** What a delegation scope can name. `all` covers every delegable category. */
export const COVERAGE_CATEGORIES = [
    'policy', 'risk_acceptance', 'access_review', 'scn', 'management_response', 'work_item',
    'certification_package', 'system_description', 'all',
];
/**
 * Signatures only the approving official (or a board-authorized alternate
 * signer) may give: the OCR / certification package and the system
 * description / management assertion (research §2 item 14); a CMMC
 * affirmation, which only the named affirming official signs (ADR-0058 §5).
 */
export const NON_DELEGABLE_CATEGORIES = ['certification_package', 'system_description', 'cmmc_affirmation'];
export function isNonDelegable(category) {
    return NON_DELEGABLE_CATEGORIES.includes(category);
}
export function delegationCovers(scopeJson, category) {
    if (isNonDelegable(category))
        return false;
    let scope;
    try {
        scope = JSON.parse(scopeJson);
    }
    catch {
        return false;
    }
    if (!Array.isArray(scope))
        return false;
    return scope.includes('all') || scope.includes(category);
}
// ---- dates / people ----------------------------------------------------------------
/** UTC calendar day of an instant. */
export function utcDay(d) {
    return d.toISOString().slice(0, 10);
}
export function addDays(day, n) {
    const d = new Date(`${day}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return utcDay(d);
}
export async function isActiveMember(db, workspaceId, userId) {
    const r = await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, userId]);
    return r.results.length > 0;
}
/** A confirmed absence covering `day` (or an assumed one, for previews). */
export async function absenceOn(db, workspaceId, userId, day, assume) {
    const hypo = assume?.absences?.find((a) => a.user_id === userId && a.starts_on <= day && a.ends_on >= day);
    if (hypo)
        return { starts_on: hypo.starts_on, ends_on: hypo.ends_on, absence_id: null };
    const r = await db.query(`SELECT * FROM absences WHERE workspace_id = ? AND user_id = ? AND state = 'confirmed' AND starts_on <= ? AND ends_on >= ?
      ORDER BY starts_on ASC LIMIT 1`, [workspaceId, userId, day, day]);
    const a = r.results[0];
    return a ? { starts_on: a.starts_on, ends_on: a.ends_on, absence_id: a.absence_id } : null;
}
/** The live delegation from `delegatorId` covering `category` at `at`, if any. */
export async function activeDelegationFor(db, workspaceId, delegatorId, category, at, assume) {
    const iso = at.toISOString();
    const hypo = assume?.delegations?.find((d) => d.delegator_user_id === delegatorId && d.starts_at <= iso && d.ends_at > iso
        && delegationCovers(d.scope_json, category));
    if (hypo)
        return hypo;
    const r = await db.query(`SELECT * FROM delegations WHERE workspace_id = ? AND delegator_user_id = ? AND ended_at IS NULL AND starts_at <= ? AND ends_at > ?
      ORDER BY created_at DESC, delegation_id ASC`, [workspaceId, delegatorId, iso, iso]);
    const ignore = new Set(assume?.ignore_delegation_ids ?? []);
    return r.results.find((d) => !ignore.has(d.delegation_id) && delegationCovers(d.scope_json, category)) ?? null;
}
/**
 * V98-3 review S1/S2: a delegation is EFFECTIVE only while the delegator is
 * away — a confirmed absence whose window has started (absenceOn). A proposed
 * absence is not an absence; a delegation set ahead of a leave waits for it.
 * Every place that moves work to a delegate or locks the delegator out (Today,
 * signing, the delegate's "covering for" list, nudges) reads this, not the
 * raw delegation window.
 */
export async function effectiveDelegationFor(db, workspaceId, delegatorId, category, at, assume) {
    if (!(await absenceOn(db, workspaceId, delegatorId, utcDay(at), assume)))
        return null;
    return activeDelegationFor(db, workspaceId, delegatorId, category, at, assume);
}
/** The approver's current escalation chain, in order (rule 16). */
export async function escalationChainOf(db, workspaceId, approverId) {
    const r = await db.query(`SELECT entry_id, approver_user_id, position, kind, escalation_user_id, contact_name, contact_email, authorization_ref
       FROM escalation_chains WHERE workspace_id = ? AND approver_user_id = ? AND ended_at IS NULL ORDER BY position ASC`, [workspaceId, approverId]);
    return r.results;
}
/** Current manager of a user (one open reporting line). */
export async function managerOf(db, workspaceId, userId) {
    const r = await db.query(`SELECT manager_user_id FROM reporting_lines WHERE workspace_id = ? AND user_id = ? AND ended_at IS NULL LIMIT 1`, [workspaceId, userId]);
    return r.results[0]?.manager_user_id ?? null;
}
/** True when `userId` reports to `bossId`, directly or indirectly (rule 11). */
export async function reportsTo(db, workspaceId, userId, bossId) {
    let cur = userId;
    const seen = new Set();
    for (let i = 0; i < 25 && cur; i++) {
        if (seen.has(cur))
            return false;
        seen.add(cur);
        cur = await managerOf(db, workspaceId, cur);
        if (cur === bossId)
            return true;
    }
    return false;
}
// ---- hats --------------------------------------------------------------------------
/** A person's hats: the union of their current assignments, grouped (no role switcher). */
export async function hatsOf(db, workspaceId, userId, now = new Date()) {
    const { assignments, responsibilities } = await listHats(db, workspaceId, userId, now);
    const by = {};
    for (const a of assignments)
        (by[a.responsibility] ??= []).push({ kind: a.subject_kind, id: a.subject_id });
    return { responsibilities, by_responsibility: by, assignments };
}
// ---- sod_events ------------------------------------------------------------------------
export const SOD_TRANSITIONS = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12', 'T13', 'T14', 'T15', 'T16', 'T17', 'config'];
let eventSeq = 0;
/**
 * Event ids sort in write order within one isolate (wall-clock ms + a
 * counter), so events that share a business `created_at` keep their order.
 */
function sodEventId() {
    eventSeq = (eventSeq + 1) % 1_679_616; // 36^4
    return `sde_${Date.now().toString(36).padStart(9, '0')}${eventSeq.toString(36).padStart(4, '0')}_${generateRandomId('x').slice(2, 12)}`;
}
/** One append-only sod_events INSERT, for use inside a batch. */
export function sodEventStatement(e) {
    return {
        sql: `INSERT INTO sod_events (event_id, workspace_id, transition, event, actor_user_id, on_behalf_of_user_id, delegation_id,
                                  record_kind, record_id, record_version, item_kind, item_id, detail_json, created_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [
            sodEventId(), e.workspace_id, e.transition, e.event, e.actor_user_id, e.on_behalf_of_user_id ?? null,
            e.delegation_id ?? null, e.record_kind ?? null, e.record_id ?? null, e.record_version ?? null, e.item_kind ?? null,
            e.item_id ?? null, e.detail === undefined ? null : JSON.stringify(e.detail), e.created_at,
        ],
    };
}
export async function recordSodEvent(db, e) {
    const s = sodEventStatement(e);
    await db.execute(s.sql, s.params);
}
export async function listSodEvents(db, workspaceId, f = {}) {
    const clauses = [];
    const params = [workspaceId];
    if (f.record_kind) {
        clauses.push('record_kind = ?');
        params.push(f.record_kind);
    }
    if (f.record_id) {
        clauses.push('record_id = ?');
        params.push(f.record_id);
    }
    if (f.item_kind) {
        clauses.push('item_kind = ?');
        params.push(f.item_kind);
    }
    if (f.item_id) {
        clauses.push('item_id = ?');
        params.push(f.item_id);
    }
    if (f.transition) {
        clauses.push('transition = ?');
        params.push(f.transition);
    }
    if (f.from) {
        clauses.push('created_at >= ?');
        params.push(f.from);
    }
    if (f.to) {
        clauses.push('created_at < ?');
        params.push(f.to);
    }
    const limit = Math.min(Math.max(f.limit ?? 500, 1), 5000);
    const r = await db.query(`SELECT * FROM sod_events WHERE workspace_id = ?${clauses.map((cl) => ' AND ' + cl).join('')} ORDER BY created_at ASC, event_id ASC LIMIT ${limit}`, params);
    return r.results;
}
async function holdersOf(db, workspaceId, subject, hats, iso) {
    if (hats.length === 0)
        return [];
    // d1-in-list: bounded — hats is a subset of the ASSIGNMENT_RESPONSIBILITIES enum
    const r = await db.query(`SELECT user_id, responsibility FROM assignments
      WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ? AND responsibility IN (${hats.map(() => '?').join(', ')})
        AND effective_from <= ? AND (effective_to IS NULL OR effective_to > ?)
      ORDER BY effective_from ASC, user_id ASC`, [workspaceId, subject.kind, subject.id, ...hats, iso, iso]);
    // Order by the caller's hat order first.
    return [...r.results].sort((a, b) => hats.indexOf(a.responsibility) - hats.indexOf(b.responsibility));
}
const APPROVER_HATS = ['approver', 'backup_approver'];
/** Active owners then admins, deterministic (role, joined date, id). */
export async function workspaceAdmins(db, workspaceId) {
    const r = await db.query(`SELECT user_id, role FROM memberships WHERE workspace_id = ? AND removed_at IS NULL AND role IN ('owner', 'admin')
      ORDER BY CASE role WHEN 'owner' THEN 0 ELSE 1 END ASC, joined_at ASC, user_id ASC`, [workspaceId]);
    return r.results;
}
/**
 * V98-3 review S6: each open independent-party seat with the ACTIVE member
 * who fills it — the account named on the seat, else the member whose email
 * is the seat's contact email (the invited person, once they accepted). A
 * seat nobody fills yet (invite pending, contact only, member removed) has
 * `user_id: null` and never counts as a party.
 */
export async function independentPartySeats(db, workspaceId) {
    const r = await db.query(`SELECT p.party_id, p.kind, p.contact_name, p.contact_email, p.user_id, p.created_at
       FROM independent_parties p WHERE p.workspace_id = ? AND p.ended_at IS NULL
      ORDER BY p.created_at ASC, p.party_id ASC`, [workspaceId]);
    if (r.results.length === 0)
        return [];
    // V98-7-5: seats are tenant-plane; members and their emails are control-plane — read apart, matched here.
    const members = await db.query(`SELECT m.user_id, u.email FROM memberships m JOIN users u ON u.user_id = m.user_id
      WHERE m.workspace_id = ? AND m.removed_at IS NULL
      ORDER BY m.joined_at ASC, m.user_id ASC`, [workspaceId]);
    const active = new Set(members.results.map((m) => m.user_id));
    const byEmail = new Map();
    for (const m of members.results) {
        const key = m.email?.toLowerCase();
        if (key && !byEmail.has(key))
            byEmail.set(key, m.user_id);
    }
    return r.results.map((p) => ({
        party_id: p.party_id, kind: p.kind, contact_name: p.contact_name, contact_email: p.contact_email, created_at: p.created_at,
        user_id: p.user_id
            ? (active.has(p.user_id) ? p.user_id : null)
            : (p.contact_email ? byEmail.get(p.contact_email.toLowerCase()) ?? null : null),
    }));
}
/** Users seated as the workspace's independent party (rule 16) — active members only (S6), oldest first. */
export async function independentPartyUsers(db, workspaceId) {
    return [...new Set((await independentPartySeats(db, workspaceId)).map((s) => s.user_id).filter(Boolean))];
}
/**
 * T1: resolve the eligible actor for a step. Read-only; callers record the
 * outcome (signature requests keep the trail; coverage writes a T1 event).
 */
export async function route(db, input) {
    const now = input.now ?? new Date();
    const iso = now.toISOString();
    const day = utcDay(now);
    const holders = input.holders ?? ['approver'];
    const backups = input.backups ?? ['backup_approver'];
    const category = input.category ?? input.subject.kind;
    const conflicts = conflictsFor(input.step, input.parties);
    const preparers = new Set((input.parties.preparer ?? []).filter(Boolean));
    const excluded = new Set((input.exclude ?? []).filter(Boolean));
    const trail = [];
    const seen = new Set();
    let selected = null;
    /** Eligibility of one person for this step, ignoring delegation. */
    const judge = async (user) => {
        if (preparers.has(user))
            return { outcome: 'skipped_preparer', pair_ids: conflicts.get(user) };
        if (excluded.has(user))
            return { outcome: 'skipped_excluded' };
        const c = conflicts.get(user);
        if (c)
            return { outcome: 'skipped_conflict', pair_ids: c };
        if (!(await isActiveMember(db, input.workspace_id, user)))
            return { outcome: 'skipped_not_member' };
        if (await isAuditFirmIdentity(db, user, now))
            return { outcome: 'skipped_audit_firm', pair_ids: ['audit_firm_client_hat'] };
        const away = await absenceOn(db, input.workspace_id, user, day, input.assume);
        if (away)
            return { outcome: 'skipped_absent', absent_until: away.ends_on };
        return { outcome: 'selected' };
    };
    const consider = async (user, responsibility, via, allowDelegation) => {
        if (selected) {
            trail.push({ user_id: user, responsibility, via, outcome: 'not_reached' });
            return;
        }
        if (seen.has(user))
            return;
        seen.add(user);
        const j = await judge(user);
        const entry = { user_id: user, responsibility, via, outcome: j.outcome };
        if (j.pair_ids)
            entry.pair_ids = j.pair_ids;
        if (j.absent_until)
            entry.absent_until = j.absent_until;
        trail.push(entry);
        if (j.outcome === 'selected') {
            selected = entry;
            return;
        }
        // Only an absent (not a conflicted) holder's delegate may act for them:
        // a delegate never satisfies a pair against the delegator.
        if (j.outcome === 'skipped_absent' && allowDelegation) {
            const dg = await activeDelegationFor(db, input.workspace_id, user, category, now, input.assume);
            if (!dg)
                return;
            const d = dg.delegate_user_id;
            const dj = await judge(d);
            const de = { user_id: d, responsibility, via: 'delegate', outcome: dj.outcome, for_user_id: user, delegation_id: dg.delegation_id };
            if (dj.pair_ids)
                de.pair_ids = dj.pair_ids;
            if (dj.absent_until)
                de.absent_until = dj.absent_until;
            trail.push(de);
            if (dj.outcome === 'selected')
                selected = de;
        }
    };
    const primary = await holdersOf(db, input.workspace_id, input.subject, holders, iso);
    for (const h of primary)
        await consider(h.user_id, h.responsibility, 'holder', true);
    const second = await holdersOf(db, input.workspace_id, input.subject, backups, iso);
    for (const h of second)
        await consider(h.user_id, h.responsibility, 'backup', true);
    const approverAssigned = [...primary, ...second].some((h) => APPROVER_HATS.includes(h.responsibility));
    if (input.escalate !== false) {
        const from = input.escalation_from ?? [...new Set(primary.map((h) => h.user_id))];
        for (const approver of from) {
            for (const e of await escalationChainOf(db, input.workspace_id, approver)) {
                const via = e.kind === 'alternate_signer' ? 'alternate_signer' : 'escalation';
                if (!e.escalation_user_id) {
                    if (!selected)
                        trail.push({ user_id: null, responsibility: e.kind, via, outcome: 'skipped_no_account', contact_name: e.contact_name ?? undefined, for_user_id: approver });
                    continue;
                }
                await consider(e.escalation_user_id, e.kind, via, false);
                const last = trail[trail.length - 1];
                if (last && last.user_id === e.escalation_user_id && !last.for_user_id)
                    last.for_user_id = approver;
            }
        }
    }
    if (!selected && input.fallback !== false && !isNonDelegable(category)) {
        if (!approverAssigned) {
            for (const a of await workspaceAdmins(db, input.workspace_id)) {
                if (selected)
                    break;
                await consider(a.user_id, `workspace_${a.role}`, 'workspace_admin', false);
            }
        }
        if (!selected && (!approverAssigned || input.step === 'access_reviewer')) {
            for (const u of await independentPartyUsers(db, input.workspace_id)) {
                if (selected)
                    break;
                await consider(u, 'independent_party', 'independent_party', false);
            }
        }
    }
    const picked = selected;
    const reasons = summarize(trail, picked === null);
    if (picked && picked.user_id) {
        return {
            status: 'routed', actor_user_id: picked.user_id, via: picked.via, responsibility: picked.responsibility,
            on_behalf_of_user_id: picked.via === 'delegate' ? picked.for_user_id ?? null : null,
            delegation_id: picked.delegation_id ?? null, reasons, trail,
        };
    }
    return { status: 'none_eligible', actor_user_id: null, via: null, responsibility: null, on_behalf_of_user_id: null, delegation_id: null, reasons, trail };
}
function summarize(trail, none) {
    const out = [];
    if (trail.length === 0)
        out.push('no_holder: nobody holds the hat for this step');
    for (const t of trail) {
        if (t.outcome === 'selected' || t.outcome === 'not_reached')
            continue;
        const who = t.user_id ?? t.contact_name ?? 'contact';
        if (t.outcome === 'skipped_conflict' || t.outcome === 'skipped_preparer')
            out.push(`conflict: ${who} (${(t.pair_ids ?? []).join(', ')})`);
        else if (t.outcome === 'skipped_absent')
            out.push(`absent: ${who} until ${t.absent_until}`);
        else if (t.outcome === 'skipped_no_account')
            out.push(`no_account: ${who} has no account to act with`);
        else
            out.push(`${t.outcome.replace('skipped_', '')}: ${who}`);
    }
    if (none && out.length === 0)
        out.push('no_holder: nobody holds the hat for this step');
    return [...new Set(out)];
}
/**
 * Is `userId` authorized to act as approver on this subject right now: they
 * hold `approver` / `backup_approver`, or are in the live escalation chain
 * (incl. a board-authorized alternate signer) of someone who does — or, when
 * nobody holds either hat (v9.6 data), they are an owner / admin or the
 * independent party (never for a non-delegable `category`).
 */
export async function isAuthorizedApprover(db, workspaceId, subject, userId, now = new Date(), opts = {}) {
    const iso = now.toISOString();
    const holders = await holdersOf(db, workspaceId, subject, ['approver', 'backup_approver'], iso);
    if (holders.some((h) => h.user_id === userId))
        return true;
    for (const h of holders) {
        if ((await escalationChainOf(db, workspaceId, h.user_id)).some((e) => e.escalation_user_id === userId))
            return true;
    }
    // Fallback authority (see RouteInput.fallback): nobody holds an approver hat
    // on the subject → owners / admins and the independent party. Who exactly
    // acts is the routed approver of record, checked by the caller.
    if (holders.length === 0 && (opts.category == null || !isNonDelegable(opts.category))) {
        if ((await workspaceAdmins(db, workspaceId)).some((a) => a.user_id === userId))
            return true;
        if ((await independentPartyUsers(db, workspaceId)).includes(userId))
            return true;
    }
    return false;
}
/**
 * Delegation check for a delegated signature (signature-request-service's
 * DelegationVerifier): the delegation is live, from the routed approver to the
 * actor, covers the artifact's category (never a non-delegable one), was set
 * by the delegator, and the delegate is not a party of the item.
 */
export async function verifyDelegationForRequest(db, input, now = new Date()) {
    const r = await db.query(`SELECT * FROM delegations WHERE delegation_id = ? AND workspace_id = ? LIMIT 1`, [input.delegation_id, input.workspace_id]);
    const d = r.results[0];
    if (!d || d.ended_at)
        return false;
    const iso = now.toISOString();
    if (!(d.starts_at <= iso && d.ends_at > iso))
        return false;
    // S1/S2: the delegation acts only while the delegator's confirmed absence has started.
    if (!(await absenceOn(db, input.workspace_id, input.delegator_user_id, utcDay(now))))
        return false;
    if (d.delegator_user_id !== input.delegator_user_id || d.delegate_user_id !== input.delegate_user_id || d.set_by !== d.delegator_user_id)
        return false;
    if (!delegationCovers(d.scope_json, input.request.artifact_kind))
        return false;
    const { step, parties } = signatureStep(input.request.artifact_kind, input.request.preparer_user_id);
    const c = conflictsFor(step, parties);
    if (c.has(input.delegate_user_id) || c.has(input.delegator_user_id))
        return false;
    if (input.delegate_user_id === input.request.requested_by_user_id)
        return false;
    return true;
}
const SUBJECT_ATTRS = ['user_id', 'email', 'user_email', 'login', 'username', 'upn', 'principal'];
/**
 * Record which workspace member (if any) each population row is. Matches the
 * row's identity attributes against members' user id / email; unmatched rows
 * are `no_person` (service accounts, outsiders). Insert-once: an existing
 * mapping is never changed. Idempotent.
 */
export async function ensureRowSubjects(db, workspaceId, reviewId, actor, now = new Date()) {
    const review = (await db.query(`SELECT population_id FROM access_reviews WHERE review_id = ? AND workspace_id = ? LIMIT 1`, [reviewId, workspaceId])).results[0];
    if (!review)
        return [];
    const [items, have, members] = await Promise.all([
        db.query(`SELECT item_key, attributes_json FROM population_items WHERE population_id = ? ORDER BY item_key ASC`, [review.population_id]),
        db.query(`SELECT item_key FROM access_review_rows WHERE review_id = ? AND workspace_id = ?`, [reviewId, workspaceId]),
        db.query(`SELECT u.user_id, u.email FROM memberships m JOIN users u ON u.user_id = m.user_id WHERE m.workspace_id = ?`, [workspaceId]),
    ]);
    const done = new Set(have.results.map((h) => h.item_key));
    const byKey = new Map();
    for (const m of members.results) {
        byKey.set(m.user_id.toLowerCase(), m.user_id);
        if (m.email)
            byKey.set(m.email.toLowerCase(), m.user_id);
    }
    const stmts = [];
    for (const it of items.results) {
        if (done.has(it.item_key))
            continue;
        let attrs = {};
        try {
            attrs = JSON.parse(it.attributes_json || '{}');
        }
        catch { /* keep {} */ }
        let subject = null;
        for (const k of SUBJECT_ATTRS) {
            const v = attrs[k];
            if (typeof v === 'string' && byKey.has(v.toLowerCase())) {
                subject = byKey.get(v.toLowerCase());
                break;
            }
        }
        stmts.push({
            sql: `INSERT INTO access_review_rows (workspace_id, review_id, item_key, subject_user_id, subject_state, mapped_by, created_at)
            SELECT ?, ?, ?, ?, ?, ?, ? WHERE NOT EXISTS (SELECT 1 FROM access_review_rows WHERE review_id = ? AND item_key = ?)`,
            params: [workspaceId, reviewId, it.item_key, subject, subject ? 'person' : 'no_person', actor, now.toISOString(), reviewId, it.item_key],
        });
    }
    for (let i = 0; i < stmts.length; i += 50)
        await db.batch(stmts.slice(i, i + 50));
    return listRowSubjects(db, workspaceId, reviewId);
}
export async function listRowSubjects(db, workspaceId, reviewId) {
    const r = await db.query(`SELECT * FROM access_review_rows WHERE review_id = ? AND workspace_id = ? ORDER BY item_key ASC`, [reviewId, workspaceId]);
    return r.results;
}
async function rowSubject(db, workspaceId, reviewId, itemKey) {
    const r = await db.query(`SELECT * FROM access_review_rows WHERE review_id = ? AND item_key = ? AND workspace_id = ? LIMIT 1`, [reviewId, itemKey, workspaceId]);
    return r.results[0] ?? null;
}
const REVIEW_HATS = ['access_reviewer'];
const REVIEW_BACKUP_HATS = ['approver', 'backup_approver'];
export const NEEDS_INDEPENDENT_PARTY_PROMPT = 'Nobody here can review this row except its own subject. Add an advisor seat or a board contact outside your reporting line (invite them as a member) — they review it.';
/**
 * T14: who reviews this row. The assigned reviewer, unless they are the row's
 * subject — then the row routes per T1 (another reviewer, the review's
 * approvers, then the reviewer's escalation chain, then — with no approver
 * assigned — an eligible owner / admin, then the workspace's independent
 * party, rule 16). Never the subject. When the only possible reviewer is the
 * subject (a one-person org) and no independent party is seated, the result
 * is `needs_independent_party` — never an empty choice, never self-review.
 */
export async function routeRowReviewer(db, workspaceId, reviewId, itemKey, now = new Date(), assume) {
    await ensureRowSubjects(db, workspaceId, reviewId, 'system:sod-service', now);
    const s = await rowSubject(db, workspaceId, reviewId, itemKey);
    const res = await route(db, {
        workspace_id: workspaceId, subject: { kind: 'access_review', id: reviewId }, step: 'access_reviewer',
        parties: { review_subject: [s?.subject_user_id] }, holders: REVIEW_HATS, backups: REVIEW_BACKUP_HATS,
        category: 'access_review', now, assume,
    });
    const extra = { subject_user_id: s?.subject_user_id ?? null, subject_known: !!s };
    const selfSubject = res.status === 'none_eligible' && res.trail.some((t) => t.user_id && t.user_id === s?.subject_user_id
        && (t.pair_ids ?? []).includes('access_reviewer_subject'));
    if (selfSubject) {
        return {
            ...res, ...extra, status: 'needs_independent_party', prompt: NEEDS_INDEPENDENT_PARTY_PROMPT,
            reasons: [...res.reasons, "needs_independent_party: only the row's subject could review it"],
        };
    }
    return { ...res, ...extra };
}
/** Who actually reviewed the row (decided it), else who it is routed to. */
async function rowReviewerOf(db, workspaceId, reviewId, itemKey, now, assume) {
    const d = await db.query(`SELECT decided_by_user_id FROM access_review_decisions WHERE review_id = ? AND item_key = ? LIMIT 1`, [reviewId, itemKey]);
    if (d.results[0])
        return d.results[0].decided_by_user_id;
    const r = await routeRowReviewer(db, workspaceId, reviewId, itemKey, now, assume);
    return r.actor_user_id;
}
/** The reciprocal row, if any: its subject reviewed this row and this row's subject reviewed it. */
export async function findReciprocalRow(db, workspaceId, reviewId, itemKey, now = new Date(), assume) {
    const me = await rowSubject(db, workspaceId, reviewId, itemKey);
    const reviewer = await rowReviewerOf(db, workspaceId, reviewId, itemKey, now, assume);
    if (!me?.subject_user_id || !reviewer)
        return null;
    const others = await db.query(`SELECT * FROM access_review_rows WHERE review_id = ? AND workspace_id = ? AND subject_user_id = ? AND item_key <> ?`, [reviewId, workspaceId, reviewer, itemKey]);
    for (const o of others.results) {
        if ((await rowReviewerOf(db, workspaceId, reviewId, o.item_key, now, assume)) === me.subject_user_id) {
            return { item_key: o.item_key, reviewer: me.subject_user_id, subject: reviewer };
        }
    }
    return null;
}
/** Route input for one row's approval step (shared by routeRowApprover and routeReviewRows). */
function rowApproverRoute(db, workspaceId, reviewId, itemKey, reviewer, subject, rec, now, assume) {
    return route(db, {
        workspace_id: workspaceId, subject: { kind: 'access_review', id: reviewId }, step: 'row_approver',
        parties: { access_reviewer: [reviewer, rec?.reviewer], review_subject: [subject, rec?.subject] },
        holders: ['approver'], backups: ['backup_approver'], category: 'access_review', now, assume,
    }).then((res) => {
        if (rec) {
            for (const t of res.trail) {
                if (t.user_id && (t.user_id === rec.reviewer || t.user_id === rec.subject) && t.pair_ids && !t.pair_ids.includes('reciprocal_reviewers')) {
                    t.pair_ids.push('reciprocal_reviewers');
                }
            }
            if (!res.reasons.some((r) => r.startsWith('reciprocal')))
                res.reasons.unshift(`reciprocal: rows ${itemKey} and ${rec.item_key} review each other — a third holder approves`);
        }
        return res;
    });
}
/**
 * T15: who approves this row — never its reviewer or its subject; a
 * reciprocal pair (A reviews B while B reviews A) goes to a third holder.
 * Candidates: the review's `approver` / `backup_approver` holders (with
 * delegates when absent), then the approver's escalation chain.
 */
export async function routeRowApprover(db, workspaceId, reviewId, itemKey, now = new Date(), assume) {
    await ensureRowSubjects(db, workspaceId, reviewId, 'system:sod-service', now);
    const s = await rowSubject(db, workspaceId, reviewId, itemKey);
    const reviewer = await rowReviewerOf(db, workspaceId, reviewId, itemKey, now, assume);
    const rec = await findReciprocalRow(db, workspaceId, reviewId, itemKey, now, assume);
    const res = await rowApproverRoute(db, workspaceId, reviewId, itemKey, reviewer, s?.subject_user_id ?? null, rec, now, assume);
    return { ...res, reviewer_user_id: reviewer, subject_user_id: s?.subject_user_id ?? null, reciprocal: rec ? { item_key: rec.item_key } : null };
}
/**
 * Every row of a review routed at once (Today / counts). Same rules and the
 * same route inputs as routeRowReviewer / routeRowApprover, memoized by what
 * the routing depends on: the subject (review step) and the reviewer,
 * subject and reciprocal partner (approval step) — so a 500-row population
 * routes once per distinct person, not once per row.
 */
export async function routeReviewRows(db, workspaceId, reviewId, now = new Date(), opts = {}) {
    const review = (await db.query(`SELECT population_id FROM access_reviews WHERE review_id = ? AND workspace_id = ? LIMIT 1`, [reviewId, workspaceId])).results[0];
    if (!review)
        return [];
    await ensureRowSubjects(db, workspaceId, reviewId, 'system:sod-service', now);
    const [items, subjects, decisions] = await Promise.all([
        db.query(`SELECT item_key FROM population_items WHERE population_id = ? ORDER BY item_key ASC`, [review.population_id]),
        listRowSubjects(db, workspaceId, reviewId),
        db.query(`SELECT item_key, decided_by_user_id FROM access_review_decisions WHERE review_id = ?`, [reviewId]),
    ]);
    const subj = new Map(subjects.map((s) => [s.item_key, s.subject_user_id]));
    const decided = new Map(decisions.results.map((d) => [d.item_key, d.decided_by_user_id]));
    const approved = opts.approved ?? new Set();
    const reviewerMemo = new Map();
    const reviewerFor = async (itemKey) => {
        const k = subj.get(itemKey) ?? `∅${subj.has(itemKey) ? '' : '?'}`;
        let r = reviewerMemo.get(k);
        if (!r) {
            r = await routeRowReviewer(db, workspaceId, reviewId, itemKey, now);
            reviewerMemo.set(k, r);
        }
        return r;
    };
    // Who reviewed (or will review) each row — needed for the reciprocal check.
    const reviewerOf = new Map();
    for (const { item_key } of items.results) {
        reviewerOf.set(item_key, decided.get(item_key) ?? (await reviewerFor(item_key)).actor_user_id);
    }
    const bySubject = new Map();
    for (const { item_key } of items.results) {
        const s = subj.get(item_key);
        if (s)
            bySubject.set(s, [...(bySubject.get(s) ?? []), item_key]);
    }
    const approverMemo = new Map();
    const out = [];
    for (const { item_key } of items.results) {
        const s = subj.get(item_key) ?? null;
        const by = decided.get(item_key) ?? null;
        const row = { item_key, subject_user_id: s, decided_by_user_id: by, reviewer: null, approver: null };
        if (!by) {
            row.reviewer = await reviewerFor(item_key);
        }
        else if (!approved.has(item_key)) {
            let rec = null;
            if (s) {
                for (const other of bySubject.get(by) ?? []) {
                    if (other !== item_key && reviewerOf.get(other) === s) {
                        rec = { item_key: other, reviewer: s, subject: by };
                        break;
                    }
                }
            }
            const k = `${by}|${s ?? ''}|${rec ? `${rec.reviewer}>${rec.subject}` : ''}`;
            let res = approverMemo.get(k);
            if (!res) {
                res = await rowApproverRoute(db, workspaceId, reviewId, item_key, by, s, rec, now);
                approverMemo.set(k, res);
            }
            row.approver = { ...res, reviewer_user_id: by, reciprocal: rec ? { item_key: rec.item_key } : null };
        }
        out.push(row);
    }
    return out;
}
const inPeriod = (t, from, to) => !!t && t >= from && t < to;
function evaluatePairs(step, parties, actors) {
    const evaluated = SOD_PAIRS.filter((p) => p.enforced_by === 'item' && stepMatches(p, step)
        && (parties[p.party] ?? []).some(Boolean)).map((p) => p.id);
    const c = conflictsFor(step, parties);
    const hit = new Set();
    for (const a of actors)
        if (a)
            for (const id of c.get(a) ?? [])
                if (id !== 'checker_independence')
                    hit.add(id);
    return { evaluated: [...new Set([...evaluated, ...hit])], hit: [...hit] };
}
/**
 * Same-person completeness over every approval in [from, to): every pair, at
 * row level. "N approvals in period · M same-person · K not determinable".
 */
export async function sodCompleteness(db, workspaceId, opts) {
    const now = opts.now ?? new Date();
    const { from, to } = opts;
    const rows = [];
    // 1. Signed signature requests: preparer ↔ approver family (by artifact kind).
    const sigs = await db.query(`SELECT request_id, artifact_kind, title, preparer_user_id, signed_by_user_id, on_behalf_of_user_id, delegation_id, signed_at, version
       FROM signature_requests WHERE workspace_id = ? AND state = 'signed' AND signed_at >= ? AND signed_at < ?
      ORDER BY signed_at ASC, request_id ASC`, [workspaceId, from, to]);
    for (const s of sigs.results) {
        const { step, parties } = signatureStep(s.artifact_kind, s.preparer_user_id);
        const approver = s.on_behalf_of_user_id ?? s.signed_by_user_id;
        const ev = evaluatePairs(step, parties, [s.signed_by_user_id, approver]);
        rows.push({
            source: 'signature_request', ref: `${s.request_id}@v${s.version}`, title: s.title, approved_at: s.signed_at,
            approver_user_id: approver, acted_by_user_id: s.signed_by_user_id, on_behalf_of_user_id: s.on_behalf_of_user_id,
            delegation_id: s.delegation_id, parties, pairs_evaluated: ev.evaluated, pairs_hit: ev.hit,
            status: ev.hit.length ? 'same_person' : 'distinct',
        });
    }
    // 2. Access reviews: row level. Completed reviews without row approvals or
    //    row subjects (history before row capture) are not determinable.
    const reviews = await db.query(`SELECT review_id, population_id, status, completed_at, created_at FROM access_reviews WHERE workspace_id = ?
      ORDER BY created_at ASC, review_id ASC`, [workspaceId]);
    for (const rv of reviews.results) {
        const approvals = await db.query(`SELECT * FROM access_review_row_approvals WHERE review_id = ? AND workspace_id = ? ORDER BY approved_at ASC, approval_id ASC`, [rv.review_id, workspaceId]);
        const latest = new Map();
        for (const a of approvals.results)
            latest.set(a.item_key, a);
        const inScope = [...latest.values()].filter((a) => inPeriod(a.approved_at, from, to));
        for (const a of inScope) {
            const parties = { access_reviewer: [a.reviewed_by_user_id], review_subject: [a.subject_user_id] };
            const ev = evaluatePairs('row_approver', parties, [a.approver_user_id, a.on_behalf_of_user_id]);
            // The review-level pair (reviewer ↔ subject) is evaluated on the same row.
            const rs = a.subject_user_id && a.subject_user_id === a.reviewed_by_user_id ? ['access_reviewer_subject'] : [];
            const hit = [...new Set([...ev.hit, ...rs])];
            rows.push({
                source: 'access_review_row', ref: `${rv.review_id}#${a.item_key}`, title: `Access review ${rv.review_id} · row ${a.item_key}`,
                approved_at: a.approved_at, approver_user_id: a.on_behalf_of_user_id ?? a.approver_user_id, acted_by_user_id: a.approver_user_id,
                on_behalf_of_user_id: a.on_behalf_of_user_id, delegation_id: a.delegation_id, parties,
                pairs_evaluated: [...new Set([...ev.evaluated, 'access_reviewer_subject'])], pairs_hit: hit,
                status: hit.length ? 'same_person' : 'distinct', comp_review_id: a.comp_review_id,
            });
        }
        if (rv.status === 'completed' && inPeriod(rv.completed_at, from, to)) {
            const items = await db.query(`SELECT item_key FROM population_items WHERE population_id = ? ORDER BY item_key ASC`, [rv.population_id]);
            const mapped = new Set((await listRowSubjects(db, workspaceId, rv.review_id)).map((m) => m.item_key));
            for (const it of items.results) {
                if (latest.has(it.item_key) && mapped.has(it.item_key))
                    continue;
                rows.push({
                    source: 'access_review_row', ref: `${rv.review_id}#${it.item_key}`, title: `Access review ${rv.review_id} · row ${it.item_key}`,
                    approved_at: rv.completed_at, approver_user_id: null, acted_by_user_id: null, on_behalf_of_user_id: null, delegation_id: null,
                    parties: {}, pairs_evaluated: ['access_reviewer_subject', 'row_reviewer_approver', 'row_subject_approver'], pairs_hit: [],
                    status: 'not_determinable', why: latest.has(it.item_key) ? 'row subject not captured' : 'no row-level approval record (history before row capture)',
                });
            }
        }
    }
    // 3. Security-risk acceptances: risk owner ↔ acceptance approver.
    const risks = await db.query(`SELECT risk_id, title, owner_user_id, accepted_by_user_id, updated_at FROM security_risks
      WHERE workspace_id = ? AND treatment = 'accept' AND accepted_by_user_id IS NOT NULL AND updated_at >= ? AND updated_at < ?`, [workspaceId, from, to]);
    for (const r of risks.results) {
        const parties = { risk_owner: [r.owner_user_id] };
        const ev = evaluatePairs('acceptance_approver', parties, [r.accepted_by_user_id]);
        rows.push({
            source: 'security_risk_acceptance', ref: r.risk_id, title: r.title, approved_at: r.updated_at,
            approver_user_id: r.accepted_by_user_id, acted_by_user_id: r.accepted_by_user_id, on_behalf_of_user_id: null, delegation_id: null,
            parties, pairs_evaluated: ['risk_owner_acceptance_approver'], pairs_hit: ev.hit,
            status: r.owner_user_id ? (ev.hit.length ? 'same_person' : 'distinct') : 'not_determinable',
            why: r.owner_user_id ? undefined : 'risk owner not recorded',
        });
    }
    // 4. Accepted weaknesses: the finding owner is the risk owner; none recorded → not determinable.
    const weak = await db.query(`SELECT w.weakness_id, w.accepted_by_user_id, w.accepted_at, f.owner_user_id, w.finding_id
       FROM accepted_weaknesses w JOIN findings f ON f.finding_id = w.finding_id AND f.workspace_id = w.workspace_id
      WHERE w.workspace_id = ? AND w.accepted_at >= ? AND w.accepted_at < ?`, [workspaceId, from, to]);
    for (const w of weak.results) {
        const parties = { risk_owner: [w.owner_user_id] };
        const ev = evaluatePairs('acceptance_approver', parties, [w.accepted_by_user_id]);
        rows.push({
            source: 'accepted_weakness', ref: w.weakness_id, title: `Accepted weakness on finding ${w.finding_id}`, approved_at: w.accepted_at,
            approver_user_id: w.accepted_by_user_id, acted_by_user_id: w.accepted_by_user_id, on_behalf_of_user_id: null, delegation_id: null,
            parties, pairs_evaluated: ['risk_owner_acceptance_approver'], pairs_hit: ev.hit,
            status: w.owner_user_id ? (ev.hit.length ? 'same_person' : 'distinct') : 'not_determinable',
            why: w.owner_user_id ? undefined : 'finding owner (risk owner) not recorded',
        });
    }
    // 5. Compensating self-approvals (same person by definition; access-review
    //    rows are already counted through their row approval).
    const comps = await db.query(`SELECT comp_review_id, item_kind, item_id, pair_id, preparer_user_id, self_approved_at, state FROM compensating_reviews
      WHERE workspace_id = ? AND self_approved_at >= ? AND self_approved_at < ? AND item_kind <> 'access_review_row'
      ORDER BY self_approved_at ASC, comp_review_id ASC`, [workspaceId, from, to]);
    for (const c of comps.results) {
        rows.push({
            source: 'compensating_review', ref: c.comp_review_id, title: `${c.item_kind} ${c.item_id} · self-approval (${c.state})`,
            approved_at: c.self_approved_at, approver_user_id: c.preparer_user_id, acted_by_user_id: c.preparer_user_id,
            on_behalf_of_user_id: null, delegation_id: null, parties: { preparer: [c.preparer_user_id] },
            pairs_evaluated: [c.pair_id], pairs_hit: [c.pair_id], status: 'same_person', comp_review_id: c.comp_review_id,
        });
    }
    rows.sort((a, b) => (a.approved_at < b.approved_at ? -1 : a.approved_at > b.approved_at ? 1 : a.ref < b.ref ? -1 : 1));
    const by_pair = {};
    for (const p of SOD_PAIRS)
        by_pair[p.id] = { evaluated: 0, same_person: 0 };
    for (const r of rows) {
        for (const id of r.pairs_evaluated) {
            by_pair[id] ??= { evaluated: 0, same_person: 0 };
            by_pair[id].evaluated++;
        }
        for (const id of r.pairs_hit) {
            by_pair[id] ??= { evaluated: 0, same_person: 0 };
            by_pair[id].same_person++;
        }
    }
    const same = rows.filter((r) => r.status === 'same_person').length;
    const nd = rows.filter((r) => r.status === 'not_determinable').length;
    // Second source (IPE): the append-only event logs, independently counted.
    const signedEv = await db.query(`SELECT request_id, event, at FROM signature_request_events WHERE workspace_id = ? AND event IN ('signed', 'undone')
      ORDER BY at ASC, event_id ASC`, [workspaceId]);
    const lastSigned = new Map();
    for (const e of signedEv.results)
        lastSigned.set(e.request_id, e.event === 'signed' ? e.at : null);
    const sigCount = [...lastSigned.values()].filter((at) => inPeriod(at, from, to)).length;
    // Row approvals: routed (row_approved) and compensated (self_approval on an access-review row).
    const rowEv = await db.query(`SELECT CASE WHEN event = 'row_approved' THEN record_id ELSE item_id END AS row_ref, created_at FROM sod_events
      WHERE workspace_id = ? AND (event = 'row_approved' OR (event = 'self_approval' AND item_kind = 'access_review_row'))
      ORDER BY created_at ASC, event_id ASC`, [workspaceId]);
    const lastRow = new Map();
    for (const e of rowEv.results)
        if (e.row_ref)
            lastRow.set(e.row_ref, e.created_at);
    const rowCount = [...lastRow.values()].filter((at) => inPeriod(at, from, to)).length;
    const selfEv = await db.query(`SELECT COUNT(*) AS n FROM sod_events WHERE workspace_id = ? AND event = 'self_approval' AND item_kind <> 'access_review_row'
        AND created_at >= ? AND created_at < ?`, [workspaceId, from, to]);
    const recomputed = sigCount + rowCount + Number(selfEv.results[0]?.n ?? 0);
    const primary = rows.filter((r) => r.source === 'signature_request' || r.source === 'compensating_review'
        || (r.source === 'access_review_row' && r.status !== 'not_determinable')).length;
    const dl = await db.query(`SELECT * FROM delegations WHERE workspace_id = ? AND starts_at < ? AND ends_at >= ? ORDER BY created_at ASC, delegation_id ASC`, [workspaceId, to, from]);
    const delegations = dl.results.map((d) => ({
        ...d, approvals_under: rows.filter((r) => r.delegation_id === d.delegation_id).length,
    }));
    const hw = async (sql) => (await db.query(sql, [workspaceId])).results[0]?.m ?? null;
    return {
        generated_at: now.toISOString(),
        parameters: { workspace_id: workspaceId, from, to },
        version_lock: {
            sod_events_through: await hw(`SELECT MAX(created_at) AS m FROM sod_events WHERE workspace_id = ?`),
            signature_events_through: await hw(`SELECT MAX(at) AS m FROM signature_request_events WHERE workspace_id = ?`),
        },
        population: rows.length, same_person: same, not_determinable: nd, by_pair,
        status: nd > 0 ? 'not_determinable' : 'complete', rows, delegations,
        reconciliation: {
            second_source: 'signature_request_events (signed − undone) + sod_events (row_approved, self_approval)',
            recomputed_approvals: recomputed, primary_approvals: primary, matches: recomputed === primary,
        },
    };
}
/** Every pair as a legend (the auditor tier lists them). */
export function listSodPairs() {
    return SOD_PAIRS;
}
// ---- V98-3 review R3-S8: routing reasons as plain sentences --------------------------------
/** Why a pair blocks someone, in words (the person is the subject of the sentence). */
const PAIR_SENTENCE = {
    preparer_approver: 'prepared this, so they can’t approve it',
    policy_author_approver: 'wrote this policy, so they can’t approve it',
    access_reviewer_subject: 'is the subject of this row, so they can’t review it',
    row_reviewer_approver: 'reviewed this row, so they can’t also approve it',
    row_subject_approver: 'is the subject of this row, so they can’t approve it',
    scn_classifier_co_approver: 'classified this change, so they can’t co-approve it',
    risk_owner_acceptance_approver: 'owns this risk, so they can’t approve accepting it',
    reciprocal_reviewers: 'reviewed the other side of a reciprocal pair, so a third person takes it',
    audit_firm_client_hat: 'is on an audit firm’s engagement and can’t act for the client',
    requester: 'requested this, so they can’t approve it',
};
/** Display names for user ids — members (current or former) of THIS workspace only; anyone else stays unnamed. */
export async function memberNames(db, workspaceId, ids) {
    const list = [...new Set([...ids].filter((x) => !!x))];
    if (list.length === 0)
        return new Map();
    const inList = sqlIn(list); // users + memberships: control plane (always D1)
    const r = await db.query(`SELECT u.user_id, u.display_name, u.email FROM users u
      WHERE u.user_id IN ${inList.sql}
        AND EXISTS (SELECT 1 FROM memberships m WHERE m.user_id = u.user_id AND m.workspace_id = ?)`, [...inList.params, workspaceId]);
    return new Map(r.results.map((u) => [u.user_id, (u.display_name || u.email || u.user_id)]));
}
/** The user ids a routing reason code names (so callers can load their names once). */
export function reasonUserIds(reasons) {
    const out = [];
    for (const r of reasons) {
        const m = /^(conflict|absent|no_account|excluded|not_member|audit_firm|preparer|absent_until): ([^\s(]+)/.exec(r);
        if (m && m[2] !== 'contact')
            out.push(m[2]);
    }
    return out;
}
/**
 * R3-S8: one routing reason code (as `route()` summarizes it — `conflict: dana
 * (preparer_approver)`, `absent: grace until 2026-10-12`, …) as a plain
 * sentence that names the person. Unknown codes pass through unchanged; the
 * codes themselves stay in the response next to these sentences.
 */
export function reasonSentence(reason, names) {
    const who = (id) => (id === 'contact' ? 'A contact' : names.get(id) ?? 'Someone');
    let m = /^conflict: (\S+) \(([^)]*)\)$/.exec(reason);
    if (m) {
        const pairs = m[2].split(',').map((s) => s.trim()).filter(Boolean);
        const why = pairs.map((p) => PAIR_SENTENCE[p]).find(Boolean) ?? 'is on both sides of this item';
        return `${who(m[1])} ${why}.`;
    }
    if ((m = /^absent: (\S+) until (\S+)$/.exec(reason)))
        return `${who(m[1])} is away until ${m[2]}.`;
    if ((m = /^no_account: (.+?) has no account to act with$/.exec(reason)))
        return `${m[1] === 'contact' ? 'A contact' : names.get(m[1]) ?? m[1]} has no account to act with.`;
    if ((m = /^excluded: (\S+)$/.exec(reason)))
        return `${who(m[1])} requested this, so they can’t approve it.`;
    if ((m = /^not_member: (\S+)$/.exec(reason)))
        return `${who(m[1])} is no longer a member of this workspace.`;
    if ((m = /^audit_firm: (\S+)$/.exec(reason)))
        return `${who(m[1])} is on an audit firm’s engagement and can’t act for the client.`;
    if (/^no_holder:/.test(reason))
        return 'Nobody holds this role yet — assign someone.';
    if (/^needs_independent_party:/.test(reason))
        return 'Only the row’s own subject could review it — add an independent party.';
    return reason;
}
/** R3-S8: every reason code as a sentence, deduplicated, in order. */
export function reasonSentences(reasons, names) {
    return [...new Set(reasons.map((r) => reasonSentence(r, names)))];
}
//# sourceMappingURL=sod-service.js.map